import React, { useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  Button,
  Stack,
  Grid,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FlagIcon from "@mui/icons-material/Flag";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useGlobalContext } from "context/GlobalContext";
import { useTaskDashboardContext } from "../context";
import { taskActionApi } from "API/apis/dashboard/taskAction";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { message } from "constants/responses/message";
import TaskPreviewModal from "components/Task/TaskPreviewModal";
import { confirmAction } from "utils/functions/confirmAction";
import { RemarksModal } from "./RemarkModal";

const cardBorderColors = {
  overdue: "#d32f2f",
  dueToday: "#ed6c02",
  acknowledge: "#0288d1",
  default: "#e2e8f0",
};
const getTaskStatusForColor = (dueDate) => {
  if (dayjs().isAfter(dueDate, "day")) {
    return "overdue";
  } else if (dayjs().isSame(dueDate, "day")) {
    return "dueToday";
  }
};
const TaskCardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "filterType" && prop !== "dueDate",
})(({ theme, filterType, dueDate }) => ({
  backgroundColor: "#fff",
  border: `1px solid #e2e8f0`,
  borderLeft: `5px solid ${
    cardBorderColors[getTaskStatusForColor(dueDate)] || cardBorderColors.default
  }`,
  borderRadius: "8px",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
}));

export const TaskCard = ({ task, filterType, isSelected, onSelect }) => {
  const { setIsLoading, setSuccess, setError } = useGlobalContext();
  const { setReloadRequired, reloadRequired } = useTaskDashboardContext();
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [commentData, setCommentData] = useState();
  const [buttonAction, setButtonAction] = useState("");

  // const isNew = dayjs().isSame(task.details.taskToOn, "day");
  const isNew = false;
  const isFlagged = task.status === "flagged";

  const handleOnCloseCommentModal = () => {
    setOpenCommentModal(false);
  };

  const handleActionSelect = (action) => {
    setButtonAction(action);
    setOpenCommentModal(true);
  };

  const handleTaskAction = async (action) => {
    // const confirmed = confirmAction(message.info.generalConfirmBoxMessage);
    // if (!confirmed) {
    //   return;
    // }
    try {
      const { data, status } = await taskActionApi({
        ids: [task.id],
        action: buttonAction,
        commentData,
      });
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setSuccess(message.success.updatedSuccessfully);
      setOpenCommentModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const renderActions = () => {
    switch (task.category) {
      case "pending":
        return (
          <Grid container>
            <Grid item xs={12} md={5} ml={1} mb={1}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleActionSelect("reject")}
                fullWidth
              >
                Reject
              </Button>
            </Grid>
            <Grid item xs={12} md={5} ml={1} mb={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleActionSelect("accept")}
                fullWidth
              >
                Accept
              </Button>
            </Grid>
          </Grid>
        );
      case "acknowledge":
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleActionSelect("acknowledge")}
          >
            Acknowledge
          </Button>
        );
      case "active":
        return (
          !task.isComplete && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleActionSelect("complete")}
            >
              Complete
            </Button>
          )
        );
      default:
        if (task.details.doneStatus) {
          return <Box color={cardBorderColors.acknowledge}>Completed</Box>;
        } else if (task.details.acceptanceStatus === "ACCEPTED") {
          return <Box color={cardBorderColors.dueToday}>Accepted</Box>;
        } else {
          return <Box color={cardBorderColors.overdue}>Pending</Box>;
        }
    }
  };

  return (
    <>
      <RemarksModal
        open={openCommentModal}
        onClose={handleOnCloseCommentModal}
        onSubmit={handleTaskAction}
        commentData={commentData}
        setCommentData={setCommentData}
      />

      <TaskCardWrapper filterType={filterType} dueDate={task.dueDate}>
        <Checkbox checked={isSelected} onChange={onSelect} />
        <Box flexGrow={1}>
          <TaskPreviewModal
            flexGrow={1}
            task={{
              title: task.details.docCode,
              docDetails: task.details.docDetails,
              detail: task.details.taskDetails,
              description: task.details.taskDescription,
              taskByName: task.details.taskByName,
              taskToName: task.details.taskToName,
              taskToOn: task.details.taskToOn,
              acceptanceStatus: task.details.acceptanceStatus,
              acceptanceComment: task.details.acceptanceComment,
              acceptanceOn: task.details.acceptanceOn,
              deadline: task.details.deadline,
              doneStatus: task.details.doneStatus,
              doneOn: task.details.doneOn,
              acknowledgeStatus: task.details.acknowledgeStatus,
              acknowledgeOn: task.details.acknowledgeOn,
              alert: task.details.alertBeforeDays,
              flagged: isFlagged,
              doneComment: task.details.doneComment,
              acknowledgeComment: task.details.acknowledgeComment,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Typography
                variant="body1"
                fontWeight="bold"
                color="text.primary"
              >
                {task.details.docCode} - {task.details.taskDetails}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {isFlagged && <FlagIcon color="error" fontSize="small" />}
                {isNew && <Chip label="NEW" color="info" size="small" />}
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              mt={1}
              flexWrap="wrap"
              color="text.secondary"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <PersonOutlineIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {task.category === "acknowledge" ||
                  task.category === "assigned"
                    ? "To: "
                    : "From: "}{" "}
                  {task.to || task.from}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  Due: {new Date(task.dueDate).toLocaleDateString()}
                </Typography>
              </Box>
            </Stack>
          </TaskPreviewModal>
        </Box>
        <Box sx={{ display: { xs: "block", sm: "block" } }}>
          {renderActions()}
        </Box>
      </TaskCardWrapper>
    </>
  );
};
